import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  MapPin, Star, Clock, Users, Calendar, Award, 
  MessageCircle, Globe, Phone, Instagram, Facebook, 
  CheckCircle, Shield, Languages, Briefcase, Car, Home, Utensils, Backpack, Plane, Lock, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { explorersApi } from '@/api';
import { ExperienceCard } from '@/components/ExperienceCard';
import { StarRating } from '@/components/StarRating';
import SocialShareButtons from '@/components/SocialShareButtons';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Function to upgrade image quality for detail pages
const upgradeImageQuality = (url) => {
  if (!url) return url;
  return url
    .replace(/w=800/g, 'w=1600')
    .replace(/w=400/g, 'w=1600')
    .replace(/q=80/g, 'q=90')
    .replace(/q=70/g, 'q=90');
};

const ExplorerProfilePage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleContact = () => {
    if (!isAuthenticated) {
      toast.error(i18n.language === 'fr' 
        ? 'Connectez-vous pour contacter ce guide' 
        : 'Please log in to contact this guide');
      navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }
    
    // If user has experiences, redirect to first experience to book/contact
    if (data?.experiences && data.experiences.length > 0) {
      navigate(`/experience/${data.experiences[0].experience_id}`);
    } else {
      toast.info(i18n.language === 'fr'
        ? 'Ce guide n\'a pas encore d\'expérience. Revenez bientôt !'
        : 'This guide has no experiences yet. Check back soon!');
    }
  };

  // Scroll to top when component mounts or id changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await explorersApi.getById(id);
        setData(result);
      } catch (error) {
        console.error('Error fetching explorer:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-gray-500">{i18n.language === 'fr' ? 'Explorateur non trouvé' : 'Explorer not found'}</p>
      </div>
    );
  }

  const { explorer, experiences, reviews } = data;

  const specialtyLabels = {
    adventure: i18n.language === 'fr' ? 'Aventure' : 'Adventure',
    food: i18n.language === 'fr' ? 'Gastronomie' : 'Food & Culinary',
    culture: i18n.language === 'fr' ? 'Culture & Art' : 'Culture & Art',
    history: i18n.language === 'fr' ? 'Histoire' : 'History',
    nature: i18n.language === 'fr' ? 'Nature' : 'Nature',
    sport: i18n.language === 'fr' ? 'Sport' : 'Sports',
    wellness: i18n.language === 'fr' ? 'Bien-être' : 'Wellness',
    nightlife: i18n.language === 'fr' ? 'Vie nocturne' : 'Nightlife'
  };

  return (
    <main className="min-h-screen pt-20 pb-12 bg-orange-50/30" data-testid="explorer-profile-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-orange-500 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Avatar */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative"
            >
              <img 
                src={upgradeImageQuality(explorer.picture) || `https://ui-avatars.com/api/?name=${encodeURIComponent(explorer.name)}&background=ffffff&color=f97316&size=160`}
                alt={explorer.name}
                className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-xl"
              />
              {explorer.verified && (
                <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center border-4 border-white">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
              )}
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-center md:text-left text-white flex-1"
            >
              <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                <h1 className="text-3xl md:text-4xl font-bold">{explorer.name}</h1>
                {explorer.verified && (
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    {i18n.language === 'fr' ? 'Vérifié' : 'Verified'}
                  </span>
                )}
              </div>
              
              {explorer.location && (
                <p className="flex items-center gap-2 justify-center md:justify-start text-white/90 mb-3">
                  <MapPin className="w-5 h-5" />
                  {explorer.location}
                </p>
              )}
              
              <div className="flex flex-wrap items-center gap-4 justify-center md:justify-start">
                {explorer.rating > 0 && (
                  <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
                    <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                    <span className="font-semibold">{explorer.rating.toFixed(1)}</span>
                    <span className="text-white/80">({explorer.review_count} {t('exp.reviews')})</span>
                  </div>
                )}
                
                {explorer.years_experience && (
                  <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
                    <Briefcase className="w-4 h-4" />
                    <span>{explorer.years_experience} {i18n.language === 'fr' ? 'ans d\'expérience' : 'years experience'}</span>
                  </div>
                )}
                
                {explorer.response_time && (
                  <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
                    <Clock className="w-4 h-4" />
                    <span>{explorer.response_time}</span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Contact & Share Buttons */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col gap-3"
            >
              <Button 
                size="lg" 
                className="bg-white text-primary hover:bg-white/90 font-semibold px-8"
                onClick={handleContact}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {i18n.language === 'fr' ? 'Contacter' : 'Contact'}
              </Button>
              <SocialShareButtons 
                url={window.location.href}
                title={`${explorer.name} - ${i18n.language === 'fr' ? 'Guide XPerience' : 'XPerience Guide'}`}
                description={explorer.bio || (i18n.language === 'fr' ? `Découvrez les expériences de ${explorer.name} sur XPerience` : `Discover ${explorer.name}'s experiences on XPerience`)}
              />
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Info */}
          <div className="space-y-6">
            {/* Bio */}
            {explorer.bio && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-white rounded-2xl p-6 shadow-md"
              >
                <h3 className="font-bold text-lg mb-3">
                  {i18n.language === 'fr' ? 'À propos' : 'About'}
                </h3>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">{explorer.bio}</p>
              </motion.div>
            )}

            {/* Languages */}
            {explorer.languages && explorer.languages.length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-md"
              >
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Languages className="w-5 h-5 text-primary" />
                  {i18n.language === 'fr' ? 'Langues parlées' : 'Languages'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {explorer.languages.map((lang, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1.5 bg-orange-100 text-primary rounded-full text-sm font-medium"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Specialties */}
            {explorer.specialties && explorer.specialties.length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="bg-white rounded-2xl p-6 shadow-md"
              >
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  {i18n.language === 'fr' ? 'Spécialités' : 'Specialties'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {explorer.specialties.map((spec, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {specialtyLabels[spec] || spec}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Certifications */}
            {explorer.certifications && explorer.certifications.length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 shadow-md"
              >
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  {i18n.language === 'fr' ? 'Certifications' : 'Certifications'}
                </h3>
                <ul className="space-y-2">
                  {explorer.certifications.map((cert, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {cert}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Services proposés */}
            {explorer.services && explorer.services.length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.22 }}
                className="bg-white rounded-2xl p-6 shadow-md"
              >
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  {i18n.language === 'fr' ? 'Services proposés' : 'Services Offered'}
                </h3>
                <div className="space-y-3">
                  {explorer.services.map((service, index) => {
                    const serviceIcons = {
                      vehicle: Car,
                      accommodation: Home,
                      equipment: Backpack,
                      meal: Utensils,
                      transfer: Plane
                    };
                    const serviceColors = {
                      vehicle: 'bg-blue-100 text-blue-600',
                      accommodation: 'bg-green-100 text-green-600',
                      equipment: 'bg-orange-100 text-orange-600',
                      meal: 'bg-yellow-100 text-yellow-600',
                      transfer: 'bg-purple-100 text-purple-600'
                    };
                    const Icon = serviceIcons[service.service_type] || Briefcase;
                    const colorClass = serviceColors[service.service_type] || 'bg-gray-100 text-gray-600';
                    
                    return (
                      <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{service.name}</span>
                            {service.price > 0 && (
                              <span className="text-primary font-semibold whitespace-nowrap">{service.price}€</span>
                            )}
                          </div>
                          {service.description && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {service.description.split('\n')[0]}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Social Links */}
            {explorer.social_links && Object.keys(explorer.social_links).length > 0 && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="bg-white rounded-2xl p-6 shadow-md"
              >
                <h3 className="font-bold text-lg mb-3">
                  {i18n.language === 'fr' ? 'Réseaux sociaux' : 'Social Media'}
                </h3>
                <div className="flex gap-3">
                  {explorer.social_links.instagram && (
                    <a 
                      href={explorer.social_links.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white hover:scale-110 transition-transform"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {explorer.social_links.facebook && (
                    <a 
                      href={explorer.social_links.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:scale-110 transition-transform"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  )}
                  {explorer.social_links.website && (
                    <a 
                      href={explorer.social_links.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-white hover:scale-110 transition-transform"
                    >
                      <Globe className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </motion.div>
            )}

            {/* Contact Info Notice */}
            {explorer.contact_hidden && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-amber-50 rounded-2xl p-6 shadow-md border border-amber-200"
                data-testid="contact-hidden-notice"
              >
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-amber-800 mb-1">
                      {i18n.language === 'fr' ? 'Coordonnées protégées' : 'Protected Contact Info'}
                    </h3>
                    <p className="text-sm text-amber-700">
                      {i18n.language === 'fr' 
                        ? 'Pour contacter ce guide directement, vous devez d\'abord effectuer une réservation. Les coordonnées seront disponibles après confirmation.'
                        : 'To contact this guide directly, you must first make a booking. Contact details will be available after confirmation.'}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Mail className="w-4 h-4 text-amber-500" />
                      <Phone className="w-4 h-4 text-amber-500" />
                      <span className="text-xs text-amber-600">
                        {i18n.language === 'fr' ? 'Disponibles après réservation' : 'Available after booking'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - Experiences & Reviews */}
          <div className="lg:col-span-2 space-y-8">
            {/* Experiences */}
            <div>
              <h2 className="text-2xl font-bold mb-6">
                {i18n.language === 'fr' ? 'Expériences proposées' : 'Available Experiences'}
                <span className="text-primary ml-2">({experiences.length})</span>
              </h2>
              
              {experiences.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-6">
                  {experiences.map((exp) => (
                    <ExperienceCard key={exp.experience_id} experience={exp} showFavorite={true} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 text-center shadow-md">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">
                    {i18n.language === 'fr' ? 'Aucune expérience disponible' : 'No experiences available'}
                  </p>
                </div>
              )}
            </div>

            {/* Reviews */}
            {reviews && reviews.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-6">
                  {i18n.language === 'fr' ? 'Avis des voyageurs' : 'Traveler Reviews'}
                  <span className="text-primary ml-2">({reviews.length})</span>
                </h2>
                
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <motion.div
                      key={review.review_id}
                      initial={{ y: 20, opacity: 0 }}
                      whileInView={{ y: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      className="bg-white rounded-xl p-5 shadow-md"
                    >
                      <div className="flex items-start gap-4">
                        <img 
                          src={review.experimenter_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.experimenter_name)}&size=48`}
                          alt={review.experimenter_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold">{review.experimenter_name}</h4>
                            <StarRating rating={review.rating} size="sm" showValue={false} />
                          </div>
                          <p className="text-gray-600">{review.comment}</p>
                          <p className="text-sm text-gray-400 mt-2">
                            {new Date(review.created_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { dateStyle: 'long' })}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ExplorerProfilePage;
